/**
 * 2-octave piano keyboard (C4–B5) designed for 2-6 year-old fingers.
 *
 * Non-negotiable input rules:
 * - `pointerdown`, never `click` (the click arrives ~100ms late and breaks chords).
 * - `touch-action: none` on the area (no accidental scroll/zoom).
 * - Multi-touch: each pointer is independent; glissando with `pointerenter`
 *   while there is an active pointer.
 */

import { createSignal, For } from "solid-js";
import type { SoundKey } from "@melodimals/game-core";
import { cn } from "@/lib/cn";
import { useAudio } from "@/providers/audio-provider";

interface WhiteKeyDef {
  key: SoundKey;
  /** Black key hanging from the gap to the right of this white key, if any. */
  sharp: SoundKey | null;
}

/** C4..B5 in 14 whites; the blacks (in flat notation) hang between them. */
const LAYOUT: WhiteKeyDef[] = [
  { key: "C4", sharp: "Db4" },
  { key: "D4", sharp: "Eb4" },
  { key: "E4", sharp: null },
  { key: "F4", sharp: "Gb4" },
  { key: "G4", sharp: "Ab4" },
  { key: "A4", sharp: "Bb4" },
  { key: "B4", sharp: null },
  { key: "C5", sharp: "Db5" },
  { key: "D5", sharp: "Eb5" },
  { key: "E5", sharp: null },
  { key: "F5", sharp: "Gb5" },
  { key: "G5", sharp: "Ab5" },
  { key: "A5", sharp: "Bb5" },
  { key: "B5", sharp: null },
];

const KEY_COLORS = [
  "bg-red-400",
  "bg-orange-400",
  "bg-amber-400",
  "bg-lime-400",
  "bg-emerald-400",
  "bg-sky-400",
  "bg-violet-400",
];

interface PianoKeyboardProps {
  /** Key lit by the visual guide of songs mode (M2). */
  highlightedKey?: SoundKey | null;
  /** Notifies each note played (song player / mini-games). */
  onNotePlayed?: (key: SoundKey) => void;
  class?: string;
}

export function PianoKeyboard(props: PianoKeyboardProps) {
  const audio = useAudio();
  const [pressed, setPressed] = createSignal<ReadonlySet<SoundKey>>(new Set());
  /** Pointers with the finger still down — enable glissando via pointerenter. */
  const activePointers = new Set<number>();

  const press = (key: SoundKey) => {
    audio.playNote("piano", key);
    props.onNotePlayed?.(key);
    setPressed((prev) => new Set(prev).add(key));
  };

  const release = (key: SoundKey) => {
    setPressed((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const keyHandlers = (key: SoundKey) => ({
    onPointerDown: (e: PointerEvent) => {
      e.preventDefault();
      activePointers.add(e.pointerId);
      press(key);
    },
    onPointerEnter: (e: PointerEvent) => {
      if (activePointers.has(e.pointerId)) {
        press(key);
      }
    },
    onPointerUp: (e: PointerEvent) => {
      activePointers.delete(e.pointerId);
      release(key);
    },
    onPointerLeave: () => {
      release(key);
    },
    onPointerCancel: (e: PointerEvent) => {
      activePointers.delete(e.pointerId);
      release(key);
    },
  });

  return (
    <div
      class={cn("relative flex h-full w-full select-none", props.class)}
      style={{ "touch-action": "none" }}
    >
      <For each={LAYOUT}>
        {(def, i) => (
          <div class="relative h-full min-w-0 flex-1">
            <button
              type="button"
              tabIndex={-1}
              aria-label={def.key}
              {...keyHandlers(def.key)}
              class={cn(
                "h-full w-full rounded-b-2xl border-2 border-black/20 transition-transform duration-75",
                "bg-white",
                pressed().has(def.key) && `scale-[0.97] ${KEY_COLORS[i() % KEY_COLORS.length]}`,
                props.highlightedKey === def.key &&
                  `${KEY_COLORS[i() % KEY_COLORS.length]} animate-pulse`,
              )}
            />
            {def.sharp && (
              <button
                type="button"
                tabIndex={-1}
                aria-label={def.sharp}
                {...keyHandlers(def.sharp)}
                class={cn(
                  "absolute -right-[22%] top-0 z-10 h-[58%] w-[44%] rounded-b-xl border-2 border-black/40",
                  "bg-zinc-900 transition-transform duration-75",
                  pressed().has(def.sharp) && "scale-[0.95] bg-zinc-600",
                  props.highlightedKey === def.sharp && "bg-zinc-500 animate-pulse",
                )}
              />
            )}
          </div>
        )}
      </For>
    </div>
  );
}
